import React, { useEffect, useMemo, useState } from "react";
import { Select, Checkbox, ConfigProvider } from "antd";
import theme from "theme";
import { apiPost } from "api/callApi";
import { useSelector } from "react-redux";
import SC from "./style";

const SelectUserCheckboxDropdown = (props) => {
  const {
    name,
    defaultValue,
    onChange,
    placeholder,
    selectRole,
    projectId,
    linkedProjectIds,
    type,
    setUserData,
    maxTagCount,
    setEmailString = () => {},
  } = props;

  const [options, setOptions] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const currentProject = useSelector(
    (state) => state?.currentProject?.data || []
  );

  const requestProjectId = useMemo(() => {
    if (type != 1) {
      return projectId;
    }

    const projectIds = Array.isArray(linkedProjectIds)
      ? linkedProjectIds
      : [linkedProjectIds || projectId];

    return [...new Set(projectIds.filter(Boolean))];
  }, [linkedProjectIds, projectId, type]);

  const defaultValueKey = JSON.stringify(defaultValue || []);
  const requestProjectIdKey = JSON.stringify(requestProjectId || []);
  const selectRoleKey = JSON.stringify(selectRole || []);

  useEffect(() => {
    const getData = async () => {
      try {
        const response = await apiPost(`/v1/getuserfromrole`, {
          roles: selectRole,
          projectId: requestProjectId,
          type: type,
        });

        const fetchedData = response.data || [];
        const fetchedOptions = fetchedData.map((role) => ({
          title: role.roleName,
          label: role.roleName,
          options: role.users.map((user) => ({
            label: user.fullName,
            value: user.id,
            email: user.email,
          })),
        }));

        setOptions(fetchedOptions);

        const userIds = fetchedOptions.flatMap((group) =>
          group.options.map((user) => user.value)
        );
        setUserData(userIds);

        if (defaultValue?.length) {
          setSelectedIds(defaultValue);

          const emailList = defaultValue
            .map(
              (id) =>
                fetchedOptions
                  .flatMap((group) => group.options)
                  .find((option) => option.value === id)?.email
            )
            .filter(Boolean);

          const emailStr = [...new Set(emailList)].join(", ");
          setEmailString(emailStr);
        }
      } catch (error) {
        console.error("Error fetching options:", error);
      }
    };

    getData();
  }, [defaultValueKey, requestProjectIdKey, selectRoleKey, type]);

  const handleGroupChange = (groupTitle, checked) => {
    const group = options.find((g) => g.title === groupTitle);
    if (!group) return;

    const groupIds = group.options.map((opt) => opt.value);
    const groupEmails = group.options.map((opt) => opt.email);

    const updatedIds = checked
      ? [...new Set([...selectedIds, ...groupIds])]
      : selectedIds.filter((id) => !groupIds.includes(id));

    setSelectedIds(updatedIds);

    const updatedEmails = updatedIds
      .map(
        (id) =>
          options
            .flatMap((group) => group.options)
            .find((option) => option.value === id)?.email
      )
      .filter(Boolean);

    const emailsString = [...new Set(updatedEmails)].join(", ");
    setEmailString(emailsString);
    onChange?.(name, updatedIds, emailsString);
  };

  const handleOptionChange = (id, checked) => {
    const updatedIds = checked
      ? [...selectedIds, id]
      : selectedIds.filter((selected) => selected !== id);

    setSelectedIds(updatedIds);

    const updatedEmails = updatedIds
      .map(
        (id) =>
          options
            .flatMap((group) => group.options)
            .find((option) => option.value === id)?.email
      )
      .filter(Boolean);

    const emailsString = [...new Set(updatedEmails)].join(", ");
    setEmailString(emailsString);
    onChange?.(name, updatedIds, emailsString);
  };

  const handleDeselect = (id) => {
    const updatedIds = selectedIds.filter((selected) => selected !== id);
    setSelectedIds(updatedIds);

    const updatedEmails = updatedIds
      .map(
        (id) =>
          options
            .flatMap((group) => group.options)
            .find((option) => option.value === id)?.email
      )
      .filter(Boolean);

    const emailsString = [...new Set(updatedEmails)].join(", ");
    setEmailString(emailsString);
    onChange?.(name, updatedIds, emailsString);
  };

  const isGroupChecked = (groupTitle) => {
    const group = options.find((g) => g.title === groupTitle);
    if (!group) return false;
    return group.options.every((opt) => selectedIds.includes(opt.value));
  };

  const isGroupIndeterminate = (groupTitle) => {
    const group = options.find((g) => g.title === groupTitle);
    if (!group) return false;
    const anySelected = group.options.some((opt) =>
      selectedIds.includes(opt.value)
    );
    return anySelected && !isGroupChecked(groupTitle);
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const filteredOptions = options
    .map((group) => ({
      ...group,
      options: group.options.filter((opt) =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    }))
    .filter((group) => group.options.length > 0);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#ED850C",
          colorBorder: theme.colors.bordertask,
          colorText: "black",
          colorTextPlaceholder: "rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      <Select
        mode="multiple"
        value={selectedIds}
        style={{ width: "100%" }}
        onDeselect={handleDeselect}
        placeholder={placeholder}
        maxTagCount={maxTagCount}
        showSearch
        onSearch={handleSearch}
        filterOption={false}
        tagRender={({ value, closable, onClose }) => {
          const user = options
            .flatMap((group) => group.options)
            .find((option) => option.value === value);
          return (
            <span
              style={{
                marginRight: 4,
                background: "#D5D5D5",
                borderRadius: "2px",
              }}
            >
              {user?.label}
              {closable && user?.label && (
                <span
                  onClick={onClose}
                  style={{ marginLeft: 4, cursor: "pointer" }}
                >
                  ×
                </span>
              )}
            </span>
          );
        }}
        dropdownRender={() => (
          <SC.ChildContainer>
            {filteredOptions.length > 0 ? (
              filteredOptions
                .sort((a, b) => a.title.localeCompare(b.title))
                .map((group) => (
                  <div key={group.title} style={{ margin: "8px 0px" }}>
                    <div
                      style={{
                        backgroundColor: "#CCCCCC",
                        padding: "5px",
                        fontWeight: "bold",
                        opacity: 0.8,
                      }}
                    >
                      <Checkbox
                        indeterminate={isGroupIndeterminate(group.title)}
                        checked={isGroupChecked(group.title)}
                        onChange={(e) =>
                          handleGroupChange(group.title, e.target.checked)
                        }
                      >
                        {group.label}
                      </Checkbox>
                    </div>
                    <div
                      style={{
                        paddingLeft: 5,
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                      }}
                    >
                      {group.options.map((option) => (
                        <Checkbox
                          key={option.value}
                          checked={selectedIds.includes(option.value)}
                          onChange={(e) =>
                            handleOptionChange(option.value, e.target.checked)
                          }
                        >
                          {option.label}
                        </Checkbox>
                      ))}
                    </div>
                  </div>
                ))
            ) : (
              <div
                style={{ textAlign: "center", padding: "10px", color: "#888" }}
              >
                No Data
              </div>
            )}
          </SC.ChildContainer>
        )}
      />
    </ConfigProvider>
  );
};

export default SelectUserCheckboxDropdown;
